import React from 'react'
import plgNotificationsAPI from 'api/plgNotifications'
import { ofType } from 'redux-observable'
import { EMPTY, merge, of } from 'rxjs'
import { catchError, filter, ignoreElements, map, switchMap, take, tap } from 'rxjs/operators'

import { ASYNC_ERROR_POSTFIX } from 'utils/ActionCreator'

import { CREDIT_NOTIFICATION_TYPES } from 'containers/Agents/creditConstants'
import {
  FETCH_APP_DATA_ASYNC,
  MERGE_AI_CREDITS_NOTIFICATION,
  UPDATE_AI_CREDITS_NOTIFICATION,
} from 'containers/App/constants'
import { COMPANY_FILTER_APPLY } from 'containers/PartnerOptions/constants'
import { TOAST_ADD } from 'containers/Toasts/constants'

import CreditsRestoredToast from 'components/pages/resolutionCenter/Report/components/CreditsRestoredToast'

import { connectSocket, disconnectSocket, reconnectSocketForTenantSwitch } from './socketClient'
import {
  CONNECTION_STATE,
  CONNECTION_STATES,
  CREDITS_EXHAUSTED,
  CREDITS_REQUESTED,
  CREDITS_RESTORED,
  PARTICIPANTS_CSV_ERROR,
  PARTICIPANTS_CSV_READY,
  PAYG_LIMIT_REACHED,
  PAYG_LIMIT_RESET,
  PAYG_LIMIT_RESTORE,
  PAYG_PROXIMITY_LIMIT_ALERT,
  PAYG_PROXIMITY_LIMIT_RESTORE,
  SIMULATIONS_EXPORT_ERROR,
  SIMULATIONS_EXPORT_READY,
} from './socketEvents'
import { fromAnySocketEvent } from './socketObservable'

const RESTORED_TOAST_ID = 'ai-credits-restored-toast'

// The snapshot bell (offline catch-up) carries the backend's raw CM bell types;
// map them to the FE enum resolveCreditNotification keys on, so the endpoint bell
// renders identically to the live socket path (IC-48328):
//   credits_exhausted (PLG)          → plg_exhausted
//   payg_proximity_limit_alert (80%) → approaching_limit
//   payg_limit_reached (100%)        → credit_limit_reached
// Unknown types pass through unchanged for forward-compat.
const BACKEND_BELL_TYPE = 'credits_exhausted'
const BACKEND_BELL_TYPE_MAP = {
  [BACKEND_BELL_TYPE]: CREDIT_NOTIFICATION_TYPES.PLG_EXHAUSTED,
  [PAYG_PROXIMITY_LIMIT_ALERT]: CREDIT_NOTIFICATION_TYPES.APPROACHING_LIMIT,
  [PAYG_LIMIT_REACHED]: CREDIT_NOTIFICATION_TYPES.CREDIT_LIMIT_REACHED,
}
export const mapBellType = (backendType) =>
  !backendType
    ? CREDIT_NOTIFICATION_TYPES.PLG_EXHAUSTED
    : (BACKEND_BELL_TYPE_MAP[backendType] ?? backendType)

// `credits_exhausted` → { type, reset_date_label }. Merged into ai_credits
// so the bell renders (PLG Experience §3.1).
export const normalizeBellCreditsExhaustedPayload = (payload) => {
  if (payload == null) return null
  if (typeof payload !== 'object') return undefined
  return {
    type: mapBellType(payload.type),
    reset_date_label: payload.reset_date_label ?? null,
  }
}

// PAYG 80% / 100% bells carry { type, reset_date_label }. Map the socket event to
// the FE bell enum (APPROACHING_LIMIT / CREDIT_LIMIT_REACHED); the copy variant
// (payAsYouGo vs prepurchase) is chosen by billing model in resolveCreditNotification
// (Purchase Flow §3.2 / §3.3).
export const normalizeBellTypePayload = (bellType) => (payload) => {
  if (payload == null) return null
  if (typeof payload !== 'object') return undefined
  return { type: bellType, reset_date_label: payload.reset_date_label ?? null }
}

// `payg_proximity_limit_restore`: usage dropped back under 80% — clear the bell
// only (leave any pending credit-request popup intact). Shallow-merge nulls the
// bell fields without wiping pending_request.
export const normalizeBellClearPayload = () => ({ type: null, reset_date_label: null })

// `credits_requested` → { user_name, others_count, latest_request_id }.
// Remapped to the ai_credits.pending_request shape the popup consumes
// (PLG Experience §3.2 / Purchase Flow §3.4).
export const normalizePopupCreditsRequestedPayload = (payload) => {
  if (payload == null) return null
  if (typeof payload !== 'object') return undefined
  return {
    pending_request: {
      request_id: payload.latest_request_id ?? null,
      requester_name: payload.user_name ?? null,
      other_requesters_count: payload.others_count ?? 0,
    },
  }
}

const isCreditsRestoredPopup = (popup) => popup?.type === CREDIT_NOTIFICATION_TYPES.CREDITS_RESTORED

// GET /notifications snapshot { bell, popup, reset_date_label } → a single
// ai_credits merge payload that rehydrates bell + popup after a reconnect/first
// paint. `popup` is the toast channel (Confluence 2134605833):
//   credits_requested → pending_request (admin credit-request toast)
//   credits_restored  → pending_request null (toast fired separately by snapshot epic)
// `reset_date_label` is returned to every user. A null bell/popup clears that
// slice (authoritative catch-up).
export const composeSnapshotMergePayload = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return null
  const { bell, popup, reset_date_label: resetDateLabel } = snapshot
  const merged = {}
  if (bell && typeof bell === 'object') {
    if (bell.type !== undefined) merged.type = mapBellType(bell.type)
    if (bell.reset_date_label !== undefined) merged.reset_date_label = bell.reset_date_label
    if (bell.expiration_date_label !== undefined)
      merged.expiration_date_label = bell.expiration_date_label
  } else {
    // Authoritative: null/missing bell clears any stale exhausted/limit bell.
    merged.type = null
  }
  if (resetDateLabel != null) merged.reset_date_label = resetDateLabel
  if (isCreditsRestoredPopup(popup) || !popup) {
    merged.pending_request = null
  } else {
    merged.pending_request = {
      request_id: popup.latest_request_id ?? null,
      requester_name: popup.user_name ?? null,
      other_requesters_count: popup.others_count ?? 0,
    }
  }
  return merged
}

export const buildCreditsRestoredToastAction = () => ({
  type: TOAST_ADD,
  toast: {
    // Figma 4584:147988 — dedicated rich card (white/blue border, coins GIF).
    // Not persistent → ToastsContainer auto-dismisses it after 5s (PLG Flow 5).
    id: RESTORED_TOAST_ID,
    component: React.createElement(CreditsRestoredToast, { id: RESTORED_TOAST_ID }),
    timeToDisappear: 5000,
  },
})

export const socketLifecycleEpic = (action$) =>
  action$.pipe(
    ofType(FETCH_APP_DATA_ASYNC.SUCCESS),
    take(1),
    switchMap(() => {
      connectSocket()
      return EMPTY
    })
  )

// Partner switch tears down the worker's shared socket and reconnects so the
// new handshake picks up the updated tenant context. All tabs see a brief
// disconnected → connecting → connected cycle, which is fine because
// selectActualCompanyId is session-cookie driven and globally consistent.
export const socketTenantSwitchEpic = (action$) =>
  action$.pipe(
    ofType(COMPANY_FILTER_APPLY),
    switchMap(() => {
      reconnectSocketForTenantSwitch()
      return of({
        type: UPDATE_AI_CREDITS_NOTIFICATION,
        payload: null,
      })
    })
  )

const is401AsyncError = (action) =>
  !!action.error &&
  typeof action.type === 'string' &&
  action.type.includes(ASYNC_ERROR_POSTFIX) &&
  Number(action.error.status) === 401

export const socketTeardownOn401Epic = (action$) =>
  action$.pipe(
    filter(is401AsyncError),
    tap(() => disconnectSocket()),
    ignoreElements()
  )

// Single routing table: server event name → { redux action type, payload normalizer }.
// Add a row here to wire a new socket-driven slice of redux state — no new epic needed.
export const SOCKET_EVENT_ACTION_MAP = {
  [CREDITS_EXHAUSTED]: {
    actionType: MERGE_AI_CREDITS_NOTIFICATION,
    normalize: normalizeBellCreditsExhaustedPayload,
  },
  [CREDITS_REQUESTED]: {
    actionType: MERGE_AI_CREDITS_NOTIFICATION,
    normalize: normalizePopupCreditsRequestedPayload,
  },
  // PAYG credit-limit bells (paid customers) — Purchase Flow §3.2 / §3.3.
  [PAYG_PROXIMITY_LIMIT_ALERT]: {
    actionType: MERGE_AI_CREDITS_NOTIFICATION,
    normalize: normalizeBellTypePayload(CREDIT_NOTIFICATION_TYPES.APPROACHING_LIMIT),
  },
  [PAYG_LIMIT_REACHED]: {
    actionType: MERGE_AI_CREDITS_NOTIFICATION,
    normalize: normalizeBellTypePayload(CREDIT_NOTIFICATION_TYPES.CREDIT_LIMIT_REACHED),
  },
  [PAYG_PROXIMITY_LIMIT_RESTORE]: {
    actionType: MERGE_AI_CREDITS_NOTIFICATION,
    normalize: normalizeBellClearPayload,
  },
}

// Tenant-wide "AI Credits are available!" toast events (all users). Restoring or
// resetting credits fires the toast and clears any stale bell/popup. PLG restore
// + the two PAYG restore/reset signals all map to the same toast (FE guide §Events).
const RESTORED_TOAST_EVENTS = [CREDITS_RESTORED, PAYG_LIMIT_RESTORE, PAYG_LIMIT_RESET]
const isRestoredToastEvent = (event) => RESTORED_TOAST_EVENTS.includes(event)

// Consumed by feature epics through fromSocketEvent, so they have no route here.
const FEATURE_EPIC_EVENTS = [
  SIMULATIONS_EXPORT_READY,
  SIMULATIONS_EXPORT_ERROR,
  PARTICIPANTS_CSV_READY,
  PARTICIPANTS_CSV_ERROR,
]
const isFeatureEpicEvent = (event) => FEATURE_EPIC_EVENTS.includes(event)

export const socketEventsEpic = () =>
  fromAnySocketEvent().pipe(
    map(({ event, payload }) => {
      const route = SOCKET_EVENT_ACTION_MAP[event]
      if (!route) return null
      const normalized = route.normalize(payload)
      if (normalized === undefined) return null
      return { type: route.actionType, payload: normalized }
    }),
    filter((action) => action !== null)
  )

// Diagnostics: surface unknown / unrouted server events so we notice missing
// wiring during dev. CONNECTION_STATE is a synthetic local meta-event, not a
// server event — exclude it from the unknown-event log.
export const socketEventsDiagnosticsEpic = () =>
  fromAnySocketEvent().pipe(
    filter(
      ({ event }) =>
        !SOCKET_EVENT_ACTION_MAP[event] &&
        !isRestoredToastEvent(event) &&
        !isFeatureEpicEvent(event)
    ),
    filter(({ event }) => !event.startsWith('__')),
    tap(({ event, payload }) => {
      // eslint-disable-next-line no-console
      console.debug('[socket] unrouted event', event, payload)
    }),
    ignoreElements()
  )

// The restore/reset events are transient and fan out to every tenant user —
// fire the "AI Credits are available!" toast on any of them (PLG Experience §5 /
// Purchase Flow §3.6; PAYG limit raise/reset). Snapshot catch-up uses the same
// toast via socketSnapshotEpic when popup.type === credits_restored.
export const socketCreditsRestoredToastEpic = () =>
  fromAnySocketEvent().pipe(
    filter(({ event }) => isRestoredToastEvent(event)),
    map(() => buildCreditsRestoredToastAction())
  )

// NOTE: the restore/reset socket events carry only a signal, not the new balance,
// so state.agents.billingStatus stays stale (often 0 remaining) after a restore.
// Re-fetching it here (dispatch GET_BILLING_STATUS_ASYNC.initiate on restore) — or
// having the socket payload include the amount — would let the balance update and
// the durable "out of credits" block (themisAnalysis.insufficientCredits) clear
// without requiring a manual retry.
//
// On restore, also clear the stale "out of credits" bell + pending-request popup
// live (the toast alone doesn't, so they'd otherwise linger until the next
// reload/reconnect). Null payload wipes the ai_credits notification slice.
export const socketCreditsRestoredClearEpic = () =>
  fromAnySocketEvent().pipe(
    filter(({ event }) => isRestoredToastEvent(event)),
    map(() => ({ type: MERGE_AI_CREDITS_NOTIFICATION, payload: null }))
  )

// A LIVE credits_restored push is the "acknowledge without a serve" case
// (dismiss-popup contract, Confluence 2134016052). The backend fires it
// fire-and-forget and can't know which users were connected, so it never
// mark-on-serves the caller's durable `restore` watermark for a user who saw the
// toast live — leaving counter > watermark, so the next snapshot would re-serve
// the same toast as bogus offline catch-up. Advance that watermark ourselves,
// exactly as credits_requested advances on dismiss (IC-48328). Monotonic +
// idempotent server-side, so double-firing with the snapshot-path dismiss
// (socketSnapshotEpic) is safe. Side-effect only.
//
// Fires on ALL restore events (PLG credits_restored + PAYG limit_restore/reset),
// matching the toast fan-out — the restore acknowledgement is billing-agnostic
// (product decision): whenever the restored toast is shown live, advance the
// `restore` watermark so the next snapshot doesn't re-serve it as offline
// catch-up. The dismiss body stays typed credits_restored (the single restore
// watermark the backend keys on).
export const socketCreditsRestoredDismissEpic = () =>
  fromAnySocketEvent().pipe(
    filter(({ event }) => isRestoredToastEvent(event)),
    switchMap(() =>
      plgNotificationsAPI.dismissPopup({ type: CREDIT_NOTIFICATION_TYPES.CREDITS_RESTORED }).pipe(
        catchError(() => EMPTY),
        ignoreElements()
      )
    )
  )

// Rehydrate bell + popup from the REST snapshot on first paint and on every
// socket (re)connect — Socket.IO has no offline replay, so this closes any gap
// for events missed while disconnected (FE connection guide).
// When popup.type === credits_restored (login catch-up), also fire the restored
// toast and advance the session seen cursor via dismiss-popup.
export const socketSnapshotEpic = (action$) =>
  merge(
    action$.pipe(ofType(FETCH_APP_DATA_ASYNC.SUCCESS), take(1)),
    fromAnySocketEvent().pipe(
      filter(
        ({ event, payload }) =>
          event === CONNECTION_STATE && payload === CONNECTION_STATES.CONNECTED
      )
    )
  ).pipe(
    switchMap(() =>
      plgNotificationsAPI.getSnapshot().pipe(
        switchMap((snapshot) => {
          const mergePayload = composeSnapshotMergePayload(snapshot)
          const actions = []
          if (mergePayload) {
            actions.push({ type: MERGE_AI_CREDITS_NOTIFICATION, payload: mergePayload })
          }
          const restoredCatchUp = isCreditsRestoredPopup(snapshot?.popup)
          if (restoredCatchUp) {
            actions.push(buildCreditsRestoredToastAction())
          }
          const dismiss$ = restoredCatchUp
            ? plgNotificationsAPI
                .dismissPopup({ type: CREDIT_NOTIFICATION_TYPES.CREDITS_RESTORED })
                .pipe(
                  catchError(() => EMPTY),
                  ignoreElements()
                )
            : EMPTY
          return merge(of(...actions), dismiss$)
        }),
        catchError(() => EMPTY)
      )
    )
  )

export default [
  socketLifecycleEpic,
  socketTenantSwitchEpic,
  socketTeardownOn401Epic,
  socketEventsEpic,
  socketCreditsRestoredToastEpic,
  socketCreditsRestoredClearEpic,
  socketCreditsRestoredDismissEpic,
  socketSnapshotEpic,
  socketEventsDiagnosticsEpic,
]
